/**
 * DepthTable — the Dashboard's depth-by-job-type table (WSRPDM-0017,
 * contract §3.1's `byJobType`). Presentational: the caller (`Dashboard.tsx`)
 * owns loading/error/empty states and only mounts this once rows exist.
 *
 * Each row always shows BOTH `jobTypeLabel(jobType)` and the raw enum (in
 * mono) — unlike the Queue table's Job type column (single label-or-raw-enum
 * display), the spec asks for both here so an operator can see the friendly
 * name and the exact wire value side by side; the `common.unmapped` chip
 * still marks an unmapped type explicitly. Columns are the 4 the spec calls
 * out (Pending / Running / Succeeded / Failed) — CLAIMED/SKIPPED are not
 * shown here (Queue.tsx is the screen for the full 6-status breakdown).
 */
import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { isUnmappedJobType, jobTypeLabel } from '@/domain/registry';
import type { QueueJobTypeCount } from '@/api/wsr/types';

interface DepthTableProps {
  rows: QueueJobTypeCount[];
}

export function DepthTable({ rows }: DepthTableProps) {
  const { t } = useCopy();

  return (
    <Table size="small" aria-label={t('dashboard.depthTable.ariaLabel', 'Queue depth by job type')}>
      <TableHead>
        <TableRow>
          <TableCell>{t('dashboard.depthTable.col.jobType', 'Job type')}</TableCell>
          <TableCell align="right">{t('dashboard.depthTable.col.pending', 'Pending')}</TableCell>
          <TableCell align="right">{t('dashboard.depthTable.col.running', 'Running')}</TableCell>
          <TableCell align="right">
            {t('dashboard.depthTable.col.succeeded', 'Succeeded')}
          </TableCell>
          <TableCell align="right">{t('dashboard.depthTable.col.failed', 'Failed')}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => {
          const unmapped = isUnmappedJobType(row.jobType, t);
          return (
            <TableRow key={row.jobType}>
              <TableCell>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                  <Typography variant="body2">{jobTypeLabel(row.jobType, t)}</Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontFamily: 'monospace' }}
                  >
                    {row.jobType}
                  </Typography>
                  {unmapped ? (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={t('common.unmapped', 'Unmapped')}
                    />
                  ) : null}
                </Box>
              </TableCell>
              <TableCell align="right">{row.countsByStatus.PENDING}</TableCell>
              <TableCell align="right">{row.countsByStatus.RUNNING}</TableCell>
              <TableCell align="right">{row.countsByStatus.SUCCEEDED}</TableCell>
              <TableCell align="right">{row.countsByStatus.FAILED}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
